import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * PDP "Order Sample" box: expands to pick a sample-product material variant
 * and adds only that SKU (never the wallpaper) to cart.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} header
 * @property {HTMLButtonElement} toggleButton
 * @property {HTMLElement} panel
 * @property {HTMLElement} [error]
 * @property {HTMLElement} [thumb]
 * @property {HTMLElement} [materialList]
 * @property {HTMLElement} [subtitle]
 * @property {HTMLButtonElement} [sampleAreaToggle]
 * @property {HTMLElement} [sampleAreaStatus]
 * @property {HTMLSelectElement} [attachmentSelect]
 * @property {HTMLScriptElement} [parentMeta]
 *
 * @extends {Component<Refs>}
 */
class ProductSampleComponent extends Component {
  requiredRefs = ['header', 'toggleButton', 'panel'];

  /** @type {string} */
  #parentColor = '';

  /** @type {{ title?: string, handle?: string, id?: string | number, variantId?: string | number, variantTitle?: string, color?: string, sampleTitle?: string, sampleHandle?: string } | null} */
  #parentMetaCache = null;

  /** @type {((event: Event) => void) | null} */
  #onSamplePickChange = null;

  /** @type {((event: Event) => void) | null} */
  #onPayloadChange = null;

  /** @type {((event: Event) => void) | null} */
  #onSampleAddToCart = null;

  connectedCallback() {
    super.connectedCallback();
    this.#parentMetaCache = this.#parseParentMeta();
    this.#parentColor = this.#parentMetaCache?.color || this.dataset.parentColor || '';
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#onSamplePickChange = (event) => {
      const detail = /** @type {CustomEvent<{ enabled?: boolean }>} */ (event).detail;
      this.#syncSampleAreaButton(Boolean(detail?.enabled));
      this.#updateSampleAreaStatus();
      this.#syncEditorSaveCta();
    };
    this.#onPayloadChange = () => {
      this.#setError('');
      this.#updateSampleAreaStatus();
      this.#syncEditorSaveCta();
    };
    this.#onSampleAddToCart = () => {
      void this.addSample();
    };
    document.addEventListener('ppe:sample-pick-change', this.#onSamplePickChange);
    document.addEventListener('ppe:payload-change', this.#onPayloadChange);
    document.addEventListener('ppe:sample-add-to-cart', this.#onSampleAddToCart);
    this.#loadVariants();
    this.#ensureDefaultMaterialSelected();
    this.#updateSampleAreaStatus();
    this.#syncEditorSaveCta();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    if (this.#onSamplePickChange) {
      document.removeEventListener('ppe:sample-pick-change', this.#onSamplePickChange);
      this.#onSamplePickChange = null;
    }
    if (this.#onPayloadChange) {
      document.removeEventListener('ppe:payload-change', this.#onPayloadChange);
      this.#onPayloadChange = null;
    }
    if (this.#onSampleAddToCart) {
      document.removeEventListener('ppe:sample-add-to-cart', this.#onSampleAddToCart);
      this.#onSampleAddToCart = null;
    }
  }

  /**
   * Expand when the collapsed header is clicked (Close uses collapseFromClose).
   * @param {Event} [event]
   */
  expandFromHeader = (event) => {
    const target = event?.target;
    if (target instanceof Element && target.closest('.product-sample__toggle')) return;
    if (this.getAttribute('aria-expanded') === 'true') return;
    event?.preventDefault();
    this.setExpanded(true);
  };

  /**
   * @param {KeyboardEvent} [event]
   */
  onHeaderKeydown = (event) => {
    if (event?.key !== 'Enter' && event?.key !== ' ') return;
    const target = event.target;
    if (target instanceof Element && target.closest('.product-sample__toggle')) return;
    event.preventDefault();
    if (this.getAttribute('aria-expanded') === 'true') {
      this.setExpanded(false);
      return;
    }
    this.setExpanded(true);
  };

  /**
   * @param {Event} [event]
   */
  collapseFromClose = (event) => {
    event?.preventDefault();
    event?.stopPropagation?.();
    this.setExpanded(false);
  };

  /**
   * Public expand/collapse for the Sample & replacement tab shell.
   * @param {boolean} expanded
   */
  setExpanded(expanded) {
    this.#setExpanded(Boolean(expanded));
  }

  /**
   * @param {Event} [event]
   */
  toggleExpanded = (event) => {
    event?.preventDefault();
    const expanded = this.getAttribute('aria-expanded') === 'true';
    this.setExpanded(!expanded);
  };

  /**
   * @param {Event} [event]
   */
  onMaterialChange = (_event) => {
    this.#syncEditorSaveCta();
  };

  /**
   * @param {Event} [_event]
   */
  onAttachmentChange = (_event) => {
    this.#setError('');
    this.#syncEditorSaveCta();
  };

  /**
   * Enter (or exit) sample-area pick mode in the print position editor.
   * @param {Event} [event]
   */
  onSampleAreaClick = (event) => {
    event?.preventDefault();
    const editor = this.#printPositionEditor();
    const currentlyOn = Boolean(editor?.samplePickMode);

    if (!editor?.setSamplePickMode) {
      this.#setError('Pattern position editor is not available on this product.');
      this.#syncSampleAreaButton(false);
      return;
    }

    const next = !currentlyOn;
    const ok = editor.setSamplePickMode(next);
    if (next && !ok) {
      this.#setError('Pattern tile is not configured, so sample area cannot be opened.');
      this.#syncSampleAreaButton(false);
      return;
    }

    this.#setError('');
    this.#syncSampleAreaButton(next && ok);
    this.#updateSampleAreaStatus();
    this.#syncEditorSaveCta();
  };

  async #loadVariants() {
    if (this.querySelector('[data-sample-material]')) {
      this.#ensureDefaultMaterialSelected();
      this.#syncEditorSaveCta();
      return;
    }

    const handle = this.dataset.sampleHandle;
    if (!handle) {
      this.#setError('No sample product handle is configured.');
      return;
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        this.#setError(`Could not load sample product “${handle}”.`);
        this.#syncEditorSaveCta();
        return;
      }

      const product = await response.json();
      if (product?.id) this.dataset.sampleProductId = String(product.id);
      if (product?.title) this.dataset.sampleProductTitle = product.title;
      this.#renderVariants(product);
    } catch (error) {
      console.error(error);
      this.#setError(`Could not load sample product “${handle}”.`);
    }

    this.#syncEditorSaveCta();
  }

  /**
   * @param {{ id?: number, variants?: Array<{ id: number, title: string, option1?: string, price: number, available?: boolean }> }} product
   */
  #renderVariants(product) {
    const list = this.refs.materialList;
    if (!list) return;

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (variants.length === 0) {
      this.#setError('This sample product has no variants.');
      return;
    }

    const radioName = this.dataset.radioName || 'sample-material';
    const minPrice = Math.min(...variants.map((variant) => Number(variant.price) || 0));
    const minFormatted = this.#formatPrice(minPrice);
    this.dataset.priceFormatted = minFormatted;
    this.dataset.singleVariant = variants.length === 1 ? 'true' : 'false';

    if (this.refs.subtitle) {
      const suffix = this.dataset.subtitleSuffix || 'samples + free shipping';
      this.refs.subtitle.textContent = `${minFormatted} ${suffix}`;
    }

    list.replaceChildren();
    list.hidden = true;
    const parent = this.#parentInfo();
    for (const [index, variant] of variants.entries()) {
      const title = variant.title && variant.title !== 'Default Title' ? variant.title : variant.option1 || 'Sample';
      const priceFormatted = this.#formatPrice(Number(variant.price) || 0);
      const item = document.createElement('li');
      item.className = 'product-sample__item';
      item.innerHTML = `
        <label class="product-sample__label">
          <input
            type="checkbox"
            class="product-sample__checkbox"
            name="${radioName}[]"
            value="${variant.id}"
            data-sample-material
            data-variant-id="${variant.id}"
            data-material-name="${this.#escapeAttr(title)}"
            data-parent-title="${this.#escapeAttr(parent.title)}"
            data-parent-handle="${this.#escapeAttr(parent.handle)}"
            data-parent-id="${this.#escapeAttr(parent.id)}"
            data-parent-variant-id="${this.#escapeAttr(parent.variantId)}"
            data-price="${Number(variant.price) || 0}"
            data-price-formatted="${this.#escapeAttr(priceFormatted)}"
            ${index === 0 ? 'checked' : ''}
          >
          <span class="product-sample__meta">
            <span class="product-sample__material">${this.#escapeHtml(title)}</span>
          </span>
        </label>
      `;
      const input = item.querySelector('input');
      input?.addEventListener('change', this.onMaterialChange);
      list.append(item);
    }
    this.#ensureDefaultMaterialSelected();
  }

  /**
   * Auto-select the first sample SKU. Material checkboxes are hidden; Choose Your
   * Material (peel/paste) is the customer-facing choice.
   */
  #ensureDefaultMaterialSelected() {
    const inputs = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('[data-sample-material]'))
    );
    if (inputs.length === 0) return;
    if (inputs.some((input) => input.checked)) return;
    inputs[0].checked = true;
  }

  /**
   * @param {number} cents
   */
  #formatPrice(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    try {
      return formatMoney(cents, format, currency);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  /**
   * @param {string} value
   */
  #escapeAttr(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  /**
   * @param {string} value
   */
  #escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * @param {Event} [event]
   */
  addSample = async (event) => {
    event?.preventDefault();
    if (this.dataset.adding === 'true') return;

    const selected = this.#selectedMaterials();
    if (selected.length === 0) {
      this.#ensureDefaultMaterialSelected();
    }
    const materials = this.#selectedMaterials();
    if (materials.length === 0) {
      this.#setError('Sample product is not available.');
      return;
    }

    const attachment = this.#attachmentValue();
    if (!attachment) {
      this.#setError('Please select a material option.');
      this.refs.attachmentSelect?.focus?.();
      return;
    }

    // Ensure the latest crop is written before reading the payload.
    this.#printPositionEditor()?.writePayload?.();
    const rawPayload = this.#patternPositionPayload();
    if (!rawPayload?.sample) {
      this.#setError('Select a sample area before adding to cart.');
      return;
    }
    const patternPayload = this.#withAttachment(rawPayload, attachment);

    this.dataset.adding = 'true';
    this.#syncEditorSaveCta();
    this.#setError('');

    const sectionIds = this.#cartSectionIds();
    /** @type {{items: Array<{id: number, quantity: number, properties?: Record<string, string>}>, sections?: string}} */
    const payload = {
      items: materials.map((input) => ({
        id: Number(input.dataset.variantId),
        quantity: 1,
        properties: this.#lineItemProperties(input, patternPayload),
      })),
    };

    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(',');
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data.status) {
        const message = data.description || data.message || 'Could not add sample to cart.';
        this.#setError(typeof message === 'string' ? message : 'Could not add sample to cart.');
        this.dispatchEvent(
          new CartErrorEvent(this.id || 'product-sample', data.message, data.description, data.errors)
        );
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-sample',
            itemCount: 1,
            productId: this.dataset.sampleProductId,
          })
        );
        return;
      }

      const cart = await this.#fetchCart();
      this.dispatchEvent(
        new CartAddEvent(cart ?? data, String(materials[0]?.dataset.variantId || ''), {
          source: 'product-sample',
          itemCount: Array.isArray(cart?.items) ? cart.items.length : materials.length,
          productId: this.dataset.sampleProductId,
          variantId: String(materials[0]?.dataset.variantId || ''),
          sections: data.sections,
        })
      );

      this.#flashAdded();
      this.#printPositionEditor()?.setSamplePickMode?.(false);
      this.#syncSampleAreaButton(false);
    } catch (error) {
      console.error(error);
      this.#setError('Could not add sample to cart.');
    } finally {
      this.dataset.adding = 'false';
      this.#syncEditorSaveCta();
    }
  };

  /**
   * @param {boolean} expanded
   */
  #setExpanded(expanded) {
    this.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.refs.panel.hidden = !expanded;
    this.refs.header.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.refs.header.tabIndex = expanded ? -1 : 0;
    if (expanded) {
      this.refs.header.removeAttribute('role');
    } else {
      this.refs.header.setAttribute('role', 'button');
    }
    this.refs.toggleButton.hidden = !expanded;
    this.refs.toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.refs.toggleButton.textContent = this.dataset.closeLabel || 'Close';
    if (expanded) {
      this.#syncSampleAreaButton();
      this.#updateSampleAreaStatus();
      this.#syncEditorSaveCta();
    }
  }

  /**
   * Reflect sample-area pick mode on the entry button (aria-pressed).
   * @param {boolean} [forced]
   */
  #syncSampleAreaButton(forced) {
    const btn = this.refs.sampleAreaToggle;
    if (!(btn instanceof HTMLButtonElement)) return;

    const editor = this.#printPositionEditor();
    const on =
      typeof forced === 'boolean'
        ? forced
        : Boolean(editor?.samplePickMode);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  /**
   * @returns {boolean}
   */
  #hasSelectedSampleArea() {
    return Boolean(this.#patternPositionPayload()?.sample);
  }

  /**
   * Show the chosen sample crop offsets once an area has been selected.
   */
  #updateSampleAreaStatus() {
    const status = this.refs.sampleAreaStatus;
    if (!(status instanceof HTMLElement)) return;

    const sample = this.#patternPositionPayload()?.sample;
    if (!sample) {
      status.textContent = '';
      status.hidden = true;
      this.#syncEditorSaveCta();
      return;
    }

    const x = Number(sample.offset_x_in);
    const y = Number(sample.offset_y_in);
    const xLabel = Number.isFinite(x) ? x.toFixed(1) : '0.0';
    const yLabel = Number.isFinite(y) ? y.toFixed(1) : '0.0';
    status.textContent = `Sample area offset: ${xLabel}" horizontal, ${yLabel}" vertical`;
    status.hidden = false;
    this.#syncEditorSaveCta();
  }

  /**
   * @returns {HTMLInputElement[]}
   */
  #selectedMaterials() {
    return /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('[data-sample-material]:checked'))
    );
  }

  /**
   * Drive the print-position-editor Save button as “Add sample to cart” while
   * sample pick mode is active (replaces the former in-box CTA).
   */
  #syncEditorSaveCta() {
    this.#ensureDefaultMaterialSelected();
    const editor = this.#printPositionEditor();
    if (!editor?.setSampleSaveLabel) return;
    if (!editor.samplePickMode) return;

    const selected = this.#selectedMaterials();
    const prefix =
      selected.length > 1
        ? this.dataset.ctaPrefixPlural || 'Add Samples to Cart — '
        : this.dataset.ctaPrefix || 'Add Sample to Cart — ';
    const fallbackPrice = this.dataset.priceFormatted || '';
    const totalCents = selected.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), 0);
    const price = selected.length > 0 ? this.#formatPrice(totalCents) : fallbackPrice;
    const label = `${prefix}${price}`.trim();
    const disabled =
      this.dataset.adding === 'true' ||
      selected.length === 0 ||
      !this.#hasSelectedSampleArea() ||
      !this.#hasAttachment();

    editor.setSampleSaveLabel(label, { disabled });
  }

  /**
   * @returns {string}
   */
  #attachmentValue() {
    const select =
      this.refs.attachmentSelect instanceof HTMLSelectElement
        ? this.refs.attachmentSelect
        : this.querySelector('[data-sample-attachment]');
    return String(select?.value || '').trim();
  }

  /**
   * @returns {boolean}
   */
  #hasAttachment() {
    return this.#attachmentValue().length > 0;
  }

  /**
   * @param {string} attachment
   * @returns {number|null}
   */
  #overlapInForAttachment(attachment) {
    const value = String(attachment || '').trim().toLowerCase();
    if (!value) return null;
    if (value.includes('paste') || value.includes('traditional')) return 0;
    return 0.5;
  }

  /**
   * @param {HTMLInputElement} selected
   * @param {object} [patternPayload]
   * @returns {Record<string, string>}
   */
  #lineItemProperties(selected, patternPayload) {
    /** @type {Record<string, string>} */
    const properties = {
      _Sample: 'yes',
    };

    const meta = this.#parentInfo();
    const parentTitle = (
      selected.dataset.parentTitle ||
      meta.title ||
      this.#domParentTitle() ||
      patternPayload?.pattern?.title ||
      ''
    ).trim();
    const parentHandle = (
      selected.dataset.parentHandle ||
      meta.handle ||
      patternPayload?.pattern?.handle ||
      ''
    ).trim();
    const parentId = (selected.dataset.parentId || meta.id || '').trim();
    const parentVariantId = (
      selected.dataset.parentVariantId ||
      this.#parentVariantId() ||
      meta.variantId ||
      ''
    ).trim();
    const color = this.#parentColorLabel(meta);

    if (parentTitle) {
      properties['Sample of'] = color && color.toLowerCase() !== parentTitle.toLowerCase()
        ? `${parentTitle} / ${color}`
        : parentTitle;
    } else if (parentHandle) {
      properties['Sample of'] = parentHandle
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }

    // Prefer space-free private keys; also store a public handle Liquid can always read.
    if (parentHandle && parentHandle !== 'wallpaper-sample') {
      properties._parent_handle = parentHandle;
      properties['Wallpaper handle'] = parentHandle;
      properties._parent_url = parentVariantId
        ? `/products/${parentHandle}?variant=${parentVariantId}`
        : `/products/${parentHandle}`;
    }
    if (parentId) properties._parent_product = parentId;
    if (parentVariantId) properties._parent_variant = parentVariantId;
    if (color) properties.Color = color;

    const attachment = this.#attachmentValue();
    if (attachment) properties.Material = attachment;

    // Prefer Material (peel/paste choice) for cart display; fall back to sample SKU title.
    const material = attachment || selected.dataset.materialName;
    // Underscore keeps a duplicate Material off checkout when we also write Material.
    // Cart/drawer still render `_Material` as "Material: …" for sample lines.
    if (material) properties._Material = material;

    if (patternPayload?.sample) {
      const enriched = {
        ...patternPayload,
        pattern: {
          ...patternPayload.pattern,
          handle: patternPayload.pattern?.handle || parentHandle || null,
          title: patternPayload.pattern?.title || parentTitle || null,
        },
      };
      if (attachment) {
        enriched.attachment = attachment;
        const overlapIn = this.#overlapInForAttachment(attachment);
        if (overlapIn != null) enriched.overlap_in = overlapIn;
      }
      properties['_Pattern position'] = JSON.stringify(enriched);
      const sample = patternPayload.sample;
      properties['Sample size'] =
        `${sample.width_in}" × ${sample.height_in}"`;
    }

    return properties;
  }

  /**
   * @param {object} payload
   * @param {string} attachment
   * @returns {object}
   */
  #withAttachment(payload, attachment) {
    const overlapIn = this.#overlapInForAttachment(attachment);
    const next = {
      ...payload,
      attachment,
    };
    if (overlapIn != null) next.overlap_in = overlapIn;
    return next;
  }

  /**
   * Read the live Print Position Editor payload from the wallpaper PDP form.
   * @returns {object|null}
   */
  #patternPositionPayload() {
    const editor = this.#printPositionEditor();
    const fromEditor = editor?.getPatternPayload?.();
    if (fromEditor) return fromEditor;

    const section = this.closest('.shopify-section');
    const input =
      section?.querySelector('input[name="properties[_Pattern position]"]') ||
      document.querySelector('input[name="properties[_Pattern position]"]');
    if (!(input instanceof HTMLInputElement) || !input.value.trim()) return null;

    try {
      const data = JSON.parse(input.value);
      return data && typeof data === 'object' ? data : null;
    } catch {
      return null;
    }
  }

  /**
   * @returns {{
   *   setSamplePickMode?: (enabled: boolean) => boolean,
   *   getPatternPayload?: () => object|null,
   *   writePayload?: () => void,
   *   setSampleSaveLabel?: (label: string, options?: { disabled?: boolean }) => void,
   *   offsetSaveBtn?: HTMLButtonElement|null,
   *   sampleEnabled?: boolean,
   *   samplePickMode?: boolean
   * } | null}
   */
  #printPositionEditor() {
    const section = this.closest('.shopify-section');
    const find = window.LaFataPrintPositionEditor?.find;
    if (typeof find === 'function') {
      return find(section) || find(document) || null;
    }

    const root =
      section?.querySelector('[data-print-position-editor]') ||
      document.querySelector('[data-print-position-editor]');
    return root?._printPositionEditor || null;
  }

  /**
   * @returns {{ title: string, handle: string, id: string, variantId: string, variantTitle: string, color: string, sampleTitle: string, sampleHandle: string }}
   */
  #parentInfo() {
    const meta = this.#parentMetaCache || this.#parseParentMeta() || {};
    // Prefer the visible PDP title — most reliable on the wallpaper page.
    const titleFromDom = this.#domParentTitle();

    return {
      title: String(titleFromDom || meta.title || this.dataset.parentProductTitle || '').trim(),
      handle: String(meta.handle || this.dataset.parentProductHandle || '').trim(),
      id: String(meta.id || this.dataset.parentProductId || '').trim(),
      variantId: String(meta.variantId || this.dataset.parentVariantId || '').trim(),
      variantTitle: String(meta.variantTitle || this.dataset.parentVariantTitle || '').trim(),
      color: String(this.#parentColor || meta.color || this.dataset.parentColor || '').trim(),
      sampleTitle: String(meta.sampleTitle || this.dataset.sampleProductTitle || '').trim(),
      sampleHandle: String(meta.sampleHandle || this.dataset.sampleHandle || '').trim(),
    };
  }

  /**
   * @returns {{ title?: string, handle?: string, id?: string | number, variantId?: string | number, variantTitle?: string, color?: string, sampleTitle?: string, sampleHandle?: string } | null}
   */
  #parseParentMeta() {
    const script =
      this.refs.parentMeta ||
      this.querySelector('script[type="application/json"][ref="parentMeta"]');
    if (!(script instanceof HTMLScriptElement) && !(script instanceof HTMLElement)) return null;
    try {
      const parsed = JSON.parse(script.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * @returns {string}
   */
  #domParentTitle() {
    const roots = [this.closest('.shopify-section'), document];
    for (const root of roots) {
      if (!root) continue;
      const heading = root.querySelector(
        '.product-details p[role="heading"], .product-information p[role="heading"], product-title p[role="heading"], [ref="productTitleLink"], .view-product-title a, h1'
      );
      const text = heading?.textContent?.replace(/\s+/g, ' ').trim();
      if (text) return text;
    }
    return '';
  }

  /**
   * @param {ReturnType<ProductSampleComponent['#parentInfo']>} [meta]
   * @returns {string}
   */
  #parentColorLabel(meta = this.#parentInfo()) {
    return (this.#parentColor || meta.color || '').trim();
  }

  #parentVariantId() {
    const section = this.closest('.shopify-section');
    const productId = this.dataset.parentProductId || String(this.#parentInfo().id || '');
    const scoped = productId
      ? section?.querySelector(`product-form-component[data-product-id="${productId}"] input[name="id"]`)
      : null;
    if (scoped instanceof HTMLInputElement && scoped.value) return scoped.value;

    const anyFormId = section?.querySelector(
      'product-form-component input[name="id"], form[data-type="add-to-cart-form"] input[name="id"]'
    );
    if (anyFormId instanceof HTMLInputElement && anyFormId.value) return anyFormId.value;

    const wpc = section?.querySelector('[data-wpc-block]');
    if (wpc instanceof HTMLElement && wpc.dataset.currentVariantId) {
      return wpc.dataset.currentVariantId;
    }

    return this.dataset.parentVariantId || '';
  }

  /**
   * @returns {string[]}
   */
  #cartSectionIds() {
    const ids = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.push(item.dataset.sectionId);
      }
    }
    return ids;
  }

  async #fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart after sample add:', error);
      return null;
    }
  }

  /**
   * @param {string} message
   */
  #setError(message) {
    const error = this.refs.error;
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  #flashAdded() {
    const editor = this.#printPositionEditor();
    const btn = editor?.offsetSaveBtn;
    const addedLabel = this.dataset.addedLabel || Theme.translations.added || 'Added';
    if (btn instanceof HTMLButtonElement) {
      btn.textContent = addedLabel;
      btn.classList.add('is-saved');
    }
    window.setTimeout(() => this.#syncEditorSaveCta(), 1500);
  }

  /**
   * @param {Event & { detail?: { data?: { productId?: string, newProduct?: { id?: string, url?: string }, html?: Document | Element }, resource?: { id?: number, title?: string, options?: string[], featured_media?: { preview_image?: { src?: string } } } } }} event
   */
  #onVariantUpdate = (event) => {
    const newProduct = event.detail?.data?.newProduct;
    if (newProduct?.id) {
      this.dataset.parentProductId = String(newProduct.id);
      if (this.#parentMetaCache) this.#parentMetaCache.id = newProduct.id;
      if (newProduct.url) {
        const path = newProduct.url.split('?')[0] || '';
        const handle = path.split('/products/').pop();
        if (handle) {
          this.dataset.parentProductHandle = handle;
          if (this.#parentMetaCache) this.#parentMetaCache.handle = handle;
        }
      }
      const html = event.detail?.data?.html;
      const titleEl =
        html instanceof Document || html instanceof Element
          ? html.querySelector?.(
              '.product-details p[role="heading"], .product-information p[role="heading"], .product-title, [data-product-title], product-title, h1'
            )
          : null;
      const titleText = titleEl?.textContent?.trim();
      if (titleText) {
        this.dataset.parentProductTitle = titleText;
        if (this.#parentMetaCache) this.#parentMetaCache.title = titleText;
      }
    }

    const productId = event.detail?.data?.productId;
    if (productId && productId !== this.dataset.parentProductId) return;

    const resource = event.detail?.resource;
    if (!resource) return;

    if (resource.id) {
      this.dataset.parentVariantId = String(resource.id);
      if (this.#parentMetaCache) this.#parentMetaCache.variantId = resource.id;
    }

    if (resource.title && resource.title !== 'Default Title') {
      this.dataset.parentVariantTitle = resource.title;
      if (this.#parentMetaCache) this.#parentMetaCache.variantTitle = resource.title;
    }

    const optionNames = this.#optionNames();
    const options = resource.options || [];
    let foundColor = false;
    for (let i = 0; i < optionNames.length; i++) {
      const name = optionNames[i]?.toLowerCase() || '';
      if (name === 'color' || name === 'colour' || name === 'colorway' || name === 'colourway') {
        this.#parentColor = options[i] || '';
        if (this.#parentColor) {
          this.dataset.parentColor = this.#parentColor;
          if (this.#parentMetaCache) this.#parentMetaCache.color = this.#parentColor;
        }
        foundColor = true;
        break;
      }
    }

    if (!foundColor && options.length === 1 && options[0]) {
      const soleName = optionNames[0]?.toLowerCase() || '';
      if (soleName !== 'material' && soleName !== 'size') {
        this.#parentColor = options[0];
        this.dataset.parentColor = options[0];
        if (this.#parentMetaCache) this.#parentMetaCache.color = options[0];
      }
    }

    const src = resource.featured_media?.preview_image?.src;
    const thumbImage = this.refs.thumb?.querySelector('img');
    if (src && thumbImage) {
      const width = thumbImage.getAttribute('width') || '120';
      thumbImage.src = src.includes('?') ? `${src}&width=${width}` : `${src}?width=${width}`;
    }
  };

  /**
   * @returns {string[]}
   */
  #optionNames() {
    try {
      return JSON.parse(this.dataset.optionNames || '[]');
    } catch {
      return [];
    }
  }
}

if (!customElements.get('product-sample-component')) {
  customElements.define('product-sample-component', ProductSampleComponent);
}
